import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Skeleton } from "@/components/base/Loading";
import { formatDate } from "@/lib/format";
import type { SystemMetric } from "@/pages/admin/monitor/hooks/useMonitorData";

interface MonitorChartsProps {
  metrics: SystemMetric[];
  loading: boolean;
}

const tooltipStyle = {
  borderRadius: "8px",
  border: "1px solid oklch(var(--background-300))",
  fontSize: "12px",
  backgroundColor: "oklch(var(--background-50))",
  color: "oklch(var(--foreground-950))",
};

const axisTick = { fontSize: 11, fill: "oklch(var(--foreground-500))" };

export default function MonitorCharts({ metrics, loading }: MonitorChartsProps) {
  const chartData = metrics.map((metric) => ({
    date: formatDate(metric.metric_date),
    激活: metric.activations,
    签发: metric.licenses_issued,
    请求: metric.api_requests,
    可用率: Number(metric.uptime),
  }));

  const empty = !loading && chartData.length === 0;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-lg border border-background-200 bg-background-50 p-5 lg:col-span-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-base font-semibold text-foreground-950">授权激活 / 签发趋势</h3>
            <p className="mt-1 text-xs text-foreground-500">终端激活量与许可证签发量的逐日变化</p>
          </div>
          <div className="flex items-center gap-4 text-xs text-foreground-600">
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-primary-500" /> 激活
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-accent-500" /> 签发
            </span>
          </div>
        </div>
        <div className="mt-6 h-[280px] w-full">
          {loading ? (
            <Skeleton className="h-full w-full" />
          ) : empty ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="monitorFillActivations" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="oklch(var(--primary-500))" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="oklch(var(--primary-500))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="monitorFillIssued" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="oklch(var(--accent-500))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="oklch(var(--accent-500))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke="oklch(var(--background-300))" vertical={false} />
                <XAxis dataKey="date" tick={axisTick} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tick={axisTick} tickLine={false} axisLine={false} width={44} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "oklch(var(--foreground-600))" }} />
                <Area type="monotone" dataKey="激活" stroke="oklch(var(--primary-500))" strokeWidth={2} fill="url(#monitorFillActivations)" />
                <Area type="monotone" dataKey="签发" stroke="oklch(var(--accent-500))" strokeWidth={2} fill="url(#monitorFillIssued)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-100 p-5">
        <h3 className="font-heading text-base font-semibold text-foreground-950">接口请求量</h3>
        <p className="mt-1 text-xs text-foreground-500">逐日接口调用规模</p>
        <div className="mt-6 h-[240px] w-full">
          {loading ? (
            <Skeleton className="h-full w-full" />
          ) : empty ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="oklch(var(--background-300))" vertical={false} />
                <XAxis dataKey="date" tick={axisTick} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tick={axisTick} tickLine={false} axisLine={false} width={44} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "oklch(var(--foreground-600))" }} cursor={{ fill: "oklch(var(--background-200))" }} />
                <Bar dataKey="请求" fill="oklch(var(--secondary-500))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-100 p-5">
        <h3 className="font-heading text-base font-semibold text-foreground-950">平台可用率</h3>
        <p className="mt-1 text-xs text-foreground-500">逐日可用率（%）</p>
        <div className="mt-6 h-[240px] w-full">
          {loading ? (
            <Skeleton className="h-full w-full" />
          ) : empty ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="oklch(var(--background-300))" vertical={false} />
                <XAxis dataKey="date" tick={axisTick} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tick={axisTick} tickLine={false} axisLine={false} width={44} domain={[90, 100]} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "oklch(var(--foreground-600))" }} />
                <Line type="monotone" dataKey="可用率" stroke="oklch(var(--primary-500))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="flex h-full items-center justify-center rounded-md border border-dashed border-background-300 text-sm text-foreground-500">
      暂无监控数据
    </div>
  );
}